import { CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTENDANCE_STATUS_CODE,
  ATTENDANCE_STATUS_LABEL,
  AttendanceStatus,
  PERMISSIONS,
  WorkSchedule,
} from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { DepartmentSelect } from '@/components/DepartmentSelect';
import { TableCard } from '@/components/TableCard';

interface AttendanceEmployee {
  id: number;
  lastName: string;
  firstName: string;
  middleName: string | null;
  personnelNumber: string;
  workSchedule: string | null;
  departmentId: number | null;
  position: { id: number; name: string } | null;
}

interface AttendanceRecordDto {
  id: number;
  employeeId: number;
  date: string;
  status: AttendanceStatus;
  hours: string | null;
  notes: string | null;
}

interface AttendanceGrid {
  year: number;
  month: number;
  daysInMonth: number;
  employees: AttendanceEmployee[];
  records: AttendanceRecordDto[];
}

/** Цвет тега по статусу — тот же порядок, что в ATTENDANCE_STATUS_CODE. */
const STATUS_COLOR: Record<AttendanceStatus, string> = {
  PRESENT: 'green',
  DAY_OFF: 'default',
  VACATION: 'blue',
  UNPAID_LEAVE: 'orange',
  SICK_LEAVE: 'red',
  BUSINESS_TRIP: 'purple',
  ABSENCE: 'volcano',
};

const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** Ячейка одного дня — показывает статус и, если есть право, открывает редактирование. */
function DayCell({
  employee,
  date,
  record,
  canManage,
  onSave,
  onClear,
}: {
  employee: AttendanceEmployee;
  date: dayjs.Dayjs;
  record: AttendanceRecordDto | undefined;
  canManage: boolean;
  onSave: (status: AttendanceStatus, hours?: number, notes?: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const isWeekend = date.day() === 0 || date.day() === 6;
  // Выходной по графику подставляется сам, пока нет явной записи —
  // хранить то, что и так следует из WorkSchedule, незачем.
  const defaultStatus: AttendanceStatus | null =
    !record && employee.workSchedule === WorkSchedule.FIVE_DAYS && isWeekend
      ? AttendanceStatus.DAY_OFF
      : null;

  const status = record?.status ?? defaultStatus;

  const content = (
    <Form
      form={form}
      layout="vertical"
      style={{ width: 220 }}
      initialValues={{
        status: record?.status ?? AttendanceStatus.PRESENT,
        hours: record?.hours ? Number(record.hours) : undefined,
        notes: record?.notes ?? undefined,
      }}
      onFinish={(values) => {
        onSave(values.status, values.hours, values.notes);
        setOpen(false);
      }}
    >
      <Form.Item name="status" label={t('Статус')} style={{ marginBottom: 8 }}>
        <Select
          options={Object.values(AttendanceStatus).map((value) => ({
            value,
            label: `${ATTENDANCE_STATUS_CODE[value]} — ${ATTENDANCE_STATUS_LABEL[value]}`,
          }))}
        />
      </Form.Item>
      <Form.Item name="notes" label={t('Примечание')} style={{ marginBottom: 8 }}>
        <Input size="small" />
      </Form.Item>
      <Space>
        <Button type="primary" size="small" htmlType="submit">
          {t('Сохранить')}
        </Button>
        {record && (
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            {t('Очистить')}
          </Button>
        )}
      </Space>
    </Form>
  );

  const cell = (
    <div
      style={{
        textAlign: 'center',
        padding: '2px 0',
        cursor: canManage ? 'pointer' : 'default',
        color: isWeekend ? '#bbb' : undefined,
      }}
    >
      {status ? (
        <Tag color={STATUS_COLOR[status]} style={{ margin: 0 }}>
          {ATTENDANCE_STATUS_CODE[status]}
        </Tag>
      ) : (
        '—'
      )}
    </div>
  );

  if (!canManage) return cell;

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      content={content}
      title={`${employee.lastName} ${employee.firstName} — ${date.format('DD.MM.YYYY')}`}
    >
      {cell}
    </Popover>
  );
}

export function AttendancePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [rangeForm] = Form.useForm();

  const canManage = can(PERMISSIONS.ATTENDANCE_MANAGE);
  const year = month.year();
  const monthNumber = month.month() + 1;

  const query = useQuery({
    queryKey: ['attendance', year, monthNumber, departmentId],
    queryFn: async () =>
      (
        await api.get<AttendanceGrid>('/attendance', {
          params: { year, month: monthNumber, departmentId },
        })
      ).data,
  });

  const recordsByKey = useMemo(() => {
    const map = new Map<string, AttendanceRecordDto>();
    for (const r of query.data?.records ?? []) {
      map.set(`${r.employeeId}:${dayjs(r.date).format('YYYY-MM-DD')}`, r);
    }
    return map;
  }, [query.data]);

  const upsertDay = useApiMutation(
    async (vars: { employeeId: number; date: string; status: AttendanceStatus; hours?: number; notes?: string }) =>
      (await api.patch(`/attendance/${vars.employeeId}/${vars.date}`, {
        status: vars.status,
        hours: vars.hours,
        notes: vars.notes,
      })).data,
    { invalidate: [['attendance']] },
  );

  const clearDay = useApiMutation(
    async (vars: { employeeId: number; date: string }) =>
      (await api.delete(`/attendance/${vars.employeeId}/${vars.date}`)).data,
    { invalidate: [['attendance']] },
  );

  const setRange = useApiMutation(
    async (values: Record<string, unknown>) => {
      const [from, to] = values.period as [dayjs.Dayjs, dayjs.Dayjs];
      const { data } = await api.post('/attendance/range', {
        employeeId: values.employeeId,
        dateFrom: from.format('YYYY-MM-DD'),
        dateTo: to.format('YYYY-MM-DD'),
        status: values.status,
        notes: values.notes,
      });
      return data;
    },
    {
      successMessage: t('Проставлено'),
      invalidate: [['attendance']],
    },
  );

  if (!can(PERMISSIONS.ATTENDANCE_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр табеля')}</Typography.Text>;
  }

  const days = Array.from({ length: query.data?.daysInMonth ?? 0 }, (_, i) => month.date(i + 1));

  const columns = [
    {
      title: t('Сотрудник'),
      key: 'employee',
      fixed: 'left' as const,
      width: 220,
      render: (_: unknown, row: AttendanceEmployee) => (
        <div>
          <div>{row.lastName} {row.firstName}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.personnelNumber}
            {row.position ? ` · ${row.position.name}` : ''}
          </Typography.Text>
        </div>
      ),
    },
    ...days.map((date) => ({
      title: (
        <div style={{ textAlign: 'center' }}>
          <div>{date.date()}</div>
          <div style={{ fontSize: 11, color: '#999' }}>{WEEKDAY_SHORT[date.day()]}</div>
        </div>
      ),
      key: date.format('YYYY-MM-DD'),
      width: 48,
      render: (_: unknown, row: AttendanceEmployee) => (
        <DayCell
          employee={row}
          date={date}
          record={recordsByKey.get(`${row.id}:${date.format('YYYY-MM-DD')}`)}
          canManage={canManage}
          onSave={(status, hours, notes) =>
            upsertDay.mutate({ employeeId: row.id, date: date.format('YYYY-MM-DD'), status, hours, notes })
          }
          onClear={() => clearDay.mutate({ employeeId: row.id, date: date.format('YYYY-MM-DD') })}
        />
      ),
    })),
  ];

  return (
    <TableCard title={t('Табель учёта рабочего времени')}>
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col>
          <DatePicker
            picker="month"
            value={month}
            allowClear={false}
            onChange={(value) => value && setMonth(value.startOf('month'))}
            format="MMMM YYYY"
          />
        </Col>
        <Col>
          <DepartmentSelect
            placeholder={t('Все подразделения')}
            style={{ width: 220 }}
            value={departmentId}
            onChange={setDepartmentId}
          />
        </Col>
      </Row>

      {canManage && (
        <Form
          form={rangeForm}
          layout="inline"
          style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}
          onFinish={(values) => setRange.mutate(values, { onSuccess: () => rangeForm.resetFields() })}
        >
          <Form.Item name="employeeId" rules={[{ required: true, message: t('Выберите сотрудника') }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('Сотрудник')}
              style={{ width: 220 }}
              options={(query.data?.employees ?? []).map((e) => ({
                value: e.id,
                label: `${e.lastName} ${e.firstName} (${e.personnelNumber})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="period" rules={[{ required: true, message: t('Укажите период') }]}>
            <DatePicker.RangePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="status" rules={[{ required: true }]} initialValue={AttendanceStatus.VACATION}>
            <Select
              style={{ width: 180 }}
              options={Object.values(AttendanceStatus).map((value) => ({
                value,
                label: `${ATTENDANCE_STATUS_CODE[value]} — ${ATTENDANCE_STATUS_LABEL[value]}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes">
            <Input placeholder={t('Примечание')} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={setRange.isPending}>
              {t('Применить к периоду')}
            </Button>
          </Form.Item>
        </Form>
      )}

      <Table
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data?.employees ?? []}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description={t('Нет сотрудников')} /> }}
      />
    </TableCard>
  );
}
